/**
 * PlatformChangesPage — Phase 24 / Task 24.9 (DEC-100). Paelode-admin operator/
 * well change review queue ("/platform/catalog/changes"). Lists state-agency diffs
 * detected by the catalog sync (Task 24.2) and lets a PLATFORM_ADMIN Approve
 * (applies the catalog change + notifies affected haulers), Reject (keep current),
 * or Defer. Clicking a row opens a detail panel with the old→new values and the
 * affected hauler tenants + their tank batteries.
 *
 * PLATFORM_ADMIN only — but note precisely where that is enforced. The ROUTE is
 * registered unconditionally in App.tsx, so any signed-in role can reach this URL
 * directly; only the nav LINK is hidden. The real gate is the server: every
 * endpoint this page calls (GET/PATCH /v1/platform/catalog/change-queue*) is
 * MANAGE_PLATFORM_CATALOG, which PLATFORM_ADMIN alone holds, so a non-admin who
 * types the URL gets 403s and an empty queue rather than data.
 *
 * web-admin (packages/web-admin) is still a skeleton, so per the Task 24.9 spec
 * this admin surface lives in web-dispatch, gated to PLATFORM_ADMIN and clearly
 * separated from the hauler views.
 */
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import {
  usePlatformChangeQueue,
  usePlatformChangeDetail,
  usePlatformChangeAction,
  type PlatformChange,
  type PlatformReviewStatus,
  type AdminAction,
} from '../features/catalog-changes/hooks';
import { linkLabel } from '../features/catalog-changes/link-label';
import { formatNumber } from '@lodex-pro/shared/number-format';

const TABS: ReadonlyArray<{ key: PlatformReviewStatus | 'ALL'; label: string }> = [
  { key: 'ALL', label: 'All' },
  { key: 'PENDING', label: 'Pending' },
  { key: 'APPROVED', label: 'Approved' },
  { key: 'REJECTED', label: 'Rejected' },
  { key: 'DEFERRED', label: 'Deferred' },
];

/** A date row's ISO value in the viewer's local time (DEC-004); text rows as sent. */
function cell(value: string | null, kind: 'text' | 'date'): string {
  if (value === null) return '—';
  if (kind === 'date') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
  }
  return value;
}

/** The change as labelled old → new rows (built by the API; no ids, no raw JSON). */
function ChangeRows({ change }: { change: PlatformChange }): React.JSX.Element {
  const rows = change.display.rows;
  return (
    <div style={{ marginTop: '12px', fontSize: '13px' }}>
      {rows.length === 0 ? (
        <div style={{ color: 'var(--color-text-tertiary)' }}>
          No field values were recorded for this change.
        </div>
      ) : (
        <table style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: 'var(--color-text-tertiary)', textAlign: 'left' }}>
              <th style={{ fontWeight: 400, paddingRight: '16px' }}>Field</th>
              <th style={{ fontWeight: 400, paddingRight: '16px' }}>Old</th>
              <th style={{ fontWeight: 400, paddingRight: '16px' }} aria-label="changes to" />
              <th style={{ fontWeight: 400 }}>New</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td style={{ paddingRight: '16px', color: 'var(--color-text-secondary)' }}>
                  {r.label}
                </td>
                <td style={{ paddingRight: '16px' }}>{cell(r.old, r.kind)}</td>
                <td style={{ paddingRight: '16px' }}>→</td>
                <td style={{ fontWeight: 600 }}>{cell(r.new, r.kind)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <details style={{ marginTop: '8px' }}>
        <summary style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)' }}>
          Show raw data
        </summary>
        <pre
          style={{ margin: '4px 0 0', fontSize: '12px', fontFamily: 'JetBrains Mono, monospace' }}
        >
          {JSON.stringify({ old: change.oldValues, new: change.newValues }, null, 1)}
        </pre>
      </details>
    </div>
  );
}

function DetailPanel({ id, onClose }: { id: string; onClose: () => void }): React.JSX.Element {
  const { data, isLoading } = usePlatformChangeDetail(id);
  const action = usePlatformChangeAction();
  const [notes, setNotes] = React.useState('');

  const run = (act: AdminAction): void => {
    action.mutate(
      { id, action: act, notes },
      {
        onSuccess: () => {
          toast.success(
            act === 'approve'
              ? 'Change approved — haulers notified'
              : act === 'reject'
                ? 'Change rejected'
                : 'Deferred',
          );
          onClose();
        },
        onError: () => toast.error('Action failed — try again'),
      },
    );
  };

  const change = data?.change;
  const canAct =
    change !== undefined &&
    (change.reviewStatus === 'PENDING' || change.reviewStatus === 'DEFERRED');

  return (
    <div
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderRadius: 'var(--radius-card)',
        padding: '16px',
        marginTop: '12px',
      }}
    >
      {isLoading || data === undefined || change === undefined ? (
        <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
      ) : (
        <>
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
          >
            <div>
              <div style={{ fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' }}>
                {change.wellName ?? change.display.summary}{' '}
                {change.apiNumber !== null ? (
                  <span style={{ color: 'var(--color-text-secondary)', fontWeight: 400 }}>
                    · {change.apiNumber}
                  </span>
                ) : null}
              </div>
              <div
                style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginTop: '4px' }}
              >
                {change.stateAgency} · {change.changeType.replace(/_/g, ' ').toLowerCase()}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>

          <ChangeRows change={change} />

          <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>
              Affected haulers ({data.affectedHaulers.length})
            </div>
            {data.affectedHaulers.length === 0 ? (
              <div style={{ fontSize: '13px', color: 'var(--color-text-tertiary)' }}>
                None — no hauler uses this well (no tank battery, pickup or disposal link).
              </div>
            ) : (
              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px' }}>
                {data.affectedHaulers.map((h) => (
                  <li key={h.tenantId}>
                    {h.tenantName} — {linkLabel(h)}
                    {h.reviewStatus !== null ? ` · ${h.reviewStatus}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {canAct ? (
            <>
              <textarea
                value={notes}
                onChange={(e): void => setNotes(e.target.value)}
                placeholder="Review notes (optional)"
                rows={2}
                style={{
                  width: '100%',
                  marginTop: '12px',
                  padding: '8px',
                  fontSize: '13px',
                  backgroundColor: 'var(--color-bg-tertiary)',
                  color: 'var(--color-text-primary)',
                  border: '1px solid var(--color-border-secondary)',
                  borderRadius: '10px',
                  resize: 'vertical',
                }}
              />
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <Button
                  type="button"
                  disabled={action.isPending}
                  onClick={(): void => run('approve')}
                >
                  Approve
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={action.isPending}
                  onClick={(): void => run('reject')}
                >
                  Reject
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={action.isPending}
                  onClick={(): void => run('defer')}
                >
                  Defer
                </Button>
              </div>
            </>
          ) : (
            <div
              style={{ marginTop: '12px', fontSize: '13px', color: 'var(--color-text-tertiary)' }}
            >
              Already {change.reviewStatus.toLowerCase()}.
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function PlatformChangesPage(): React.JSX.Element {
  const [tab, setTab] = React.useState<PlatformReviewStatus | 'ALL'>('PENDING');
  const [openId, setOpenId] = React.useState<string | null>(null);
  const { data, isLoading } = usePlatformChangeQueue(tab === 'ALL' ? undefined : tab);
  const changes = data?.changes ?? [];

  return (
    <div style={{ padding: '20px', maxWidth: '1000px' }}>
      <h1 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '4px' }}>
        Catalog Change Queue
      </h1>
      <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}>
        State-agency operator/well changes detected by sync. Nothing reaches a hauler until you
        approve.
      </p>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={(): void => {
              setTab(t.key);
              setOpenId(null);
            }}
            style={{
              fontSize: '13px',
              padding: '6px 12px',
              borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--color-border-secondary)',
              cursor: 'pointer',
              backgroundColor: tab === t.key ? 'var(--color-accent-blue)' : 'transparent',
              color: tab === t.key ? 'var(--color-on-accent)' : 'var(--color-text-secondary)',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
      ) : changes.length === 0 ? (
        <p style={{ color: 'var(--color-text-secondary)' }}>No changes in this view.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {changes.map((c) => (
            <React.Fragment key={c.id}>
              <button
                type="button"
                onClick={(): void => setOpenId(openId === c.id ? null : c.id)}
                style={{
                  textAlign: 'left',
                  backgroundColor: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border-primary)',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '16px',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span
                    style={{
                      fontWeight: 600,
                      fontFamily: 'JetBrains Mono, monospace',
                      fontSize: '14px',
                    }}
                  >
                    {c.wellName ?? c.display.summary}{' '}
                    {c.apiNumber !== null ? (
                      <span style={{ color: 'var(--color-text-secondary)', fontWeight: 400 }}>
                        · {c.apiNumber}
                      </span>
                    ) : null}
                  </span>
                  <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                    {c.stateAgency} · {c.display.summary}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                  <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                    {formatNumber(c.affectedHaulerCount)} hauler
                    {c.affectedHaulerCount === 1 ? '' : 's'}
                  </span>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      color: 'var(--color-text-tertiary)',
                    }}
                  >
                    {c.reviewStatus}
                  </span>
                </div>
              </button>
              {openId === c.id ? (
                <DetailPanel id={c.id} onClose={(): void => setOpenId(null)} />
              ) : null}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
